import { APIConnectionRecord, APIConnectionUsersRecord } from '@tassat/fred-definitions';
import { UserRecord } from '@tassat/payments-definitions';
import { ActionTypes } from '@tassat/ui-definitions';
import { Dispatch, SetStateAction } from 'react';

import { ClientUserFormProps, IErrorValues, IFormValues } from './types';
import {
    firmNameAndWalletTextValidation,
    textFieldCharacterWhitelist,
    validateDescriptionInput,
    validateEmailFormatting,
    validateFullNameUniqueness,
    validateNoExcelFormulaStartingCharacters,
    validateNoNonPrintable,
    validateRequired,
    validateSsoUserIdFormatting,
    validateUsernameFormatting
} from '../helpers/validationHelpers';

export const validateClientUser = async (values: IFormValues, initialValues: IFormValues, opts: {
    actionType: ActionTypes;
    users: {
        [key: string]: UserRecord;
    };
    firmId?: string;
    isUserMappedToApiConnection: boolean;
    features?: ClientUserFormProps['features'];
    debouncedUsernameValidation: (value: string) => Promise<string | null>;
    isUsernameValidated: boolean;
    setIsUsernameValidated: Dispatch<SetStateAction<boolean>>;
    isUserMappedToSecondaryApi?: {
        apiConnectionUser: APIConnectionUsersRecord;
        apiConnection: APIConnectionRecord;
    }[];
}) => {
    const {
        actionType,
        users,
        firmId,
        isUserMappedToApiConnection,
        features,
        debouncedUsernameValidation,
        isUsernameValidated,
        setIsUsernameValidated,
        isUserMappedToSecondaryApi
    } = opts;

    const errors: IErrorValues = {} as IErrorValues;

    const firstName = values.FirstName.trim();
    const lastName = values.LastName.trim();
    const notes = values.Notes?.trim();
    const email = values.Email.trim();
    const phone = values.Phone.trim();
    const username = values.Username.trim();

    const fullName = `${firstName} ${lastName}`;
    const initalFullName = `${initialValues.FirstName} ${initialValues.LastName}`;

    const emailFormattingError = validateEmailFormatting(email, true);
    const emailExcelError = validateNoExcelFormulaStartingCharacters(email);
    const emailNonPrintableError = validateNoNonPrintable(email);

    const nameError = validateFullNameUniqueness(fullName, users, firmId);

    const firstNameRequiredError = validateRequired(firstName);
    const firstNameGarbageCharsError = validateDescriptionInput(firstName, 'First Name');
    const firstNameExcelError = validateNoExcelFormulaStartingCharacters(firstName);
    const firstNameNonPrintableError = validateNoNonPrintable(firstName);
    const firstNameValidateNoSpecialCharactersError = firmNameAndWalletTextValidation(firstName);

    const lastNameRequiredError = validateRequired(lastName);
    const lastNameGarbageCharsError = validateDescriptionInput(lastName, 'Last Name');
    const lastNameExcelError = validateNoExcelFormulaStartingCharacters(lastName);
    const lastNameNonPrintableError = validateNoNonPrintable(lastName);
    const lastNameValidateNoSpecialCharactersError = firmNameAndWalletTextValidation(lastName);

    const notesExcelError = validateNoExcelFormulaStartingCharacters(notes);

    const phoneExcelError = validateNoExcelFormulaStartingCharacters(phone);

    const usernameRequiredError = validateRequired(username);
    let usernameFormattingError = features?.SSO ?
        validateSsoUserIdFormatting(username, true)
        : validateUsernameFormatting(username, true);

    if (features?.loginType === 'Auth0' && !features?.SSO) {
        usernameFormattingError = null;
    }

    if (firstNameRequiredError) {
        errors.FirstName = firstNameRequiredError;
    } else if (firstNameExcelError) {
        errors.FirstName = firstNameExcelError;
    } else if (firstNameGarbageCharsError) {
        errors.FirstName = firstNameGarbageCharsError;
    } else if (firstNameNonPrintableError) {
        errors.FirstName = firstNameNonPrintableError;
    } else if (firstNameValidateNoSpecialCharactersError) {
        errors.FirstName = firstNameValidateNoSpecialCharactersError;
    }

    if (lastNameRequiredError) {
        errors.LastName = lastNameRequiredError;
    } else if (lastNameExcelError) {
        errors.LastName = lastNameExcelError;
    } else if (lastNameGarbageCharsError) {
        errors.LastName = lastNameGarbageCharsError;
    } else if (lastNameNonPrintableError) {
        errors.LastName = lastNameNonPrintableError;
    } else if (lastNameValidateNoSpecialCharactersError) {
        errors.LastName = lastNameValidateNoSpecialCharactersError;
    }

    if (nameError &&
        (actionType === ActionTypes.add ||
            (actionType === ActionTypes.edit && fullName !== initalFullName))) {
        errors.FirstName = nameError;
        errors.LastName = nameError;
    }

    if (emailFormattingError) {
        errors.Email = emailFormattingError;
    } else if (emailExcelError) {
        errors.Email = emailExcelError;
    } else if (emailNonPrintableError) {
        errors.Email = emailNonPrintableError;
    }

    if (notesExcelError) {
        errors.Notes = notesExcelError;
    } else {
        const notesWhitelistCheckError = textFieldCharacterWhitelist(notes);
        if (notesWhitelistCheckError) errors.Notes = notesWhitelistCheckError;
    }

    if ((initialValues.IsTokenAPIUser && !values.IsTokenAPIUser) && isUserMappedToApiConnection) {
        errors.IsTokenAPIUser = 'User is Mapped to an Api Connection, User must be enabled for TAPI';
    }

    if (phoneExcelError) {
        errors.Phone = phoneExcelError;
    } else {
        const phoneWhitelistCheckError = textFieldCharacterWhitelist(phone);
        if (phoneWhitelistCheckError) errors.Phone = phoneWhitelistCheckError;
    }

    if (usernameRequiredError) {
        errors.Username = usernameRequiredError;
    }

    if (usernameFormattingError) {
        errors.Username = usernameFormattingError;
    }

    if (!usernameFormattingError) {
        if (features?.loginType !== 'Auth0' || features?.SSO) {
            if (actionType === ActionTypes.add || actionType === ActionTypes.sso_validate){
                if (!isUsernameValidated) {
                    const error = await debouncedUsernameValidation(username);
                    if (error) {
                        errors.Username = error;
                    } else {
                        setIsUsernameValidated(true);
                    }
                }
            }
        }
    }

    if (features?.isSecondaryApiEnabled) {
        if (initialValues.APISecondaryConnectionType && !values.APISecondaryConnectionType && isUserMappedToSecondaryApi) {
            errors.APISecondaryConnectionType = 'User should be unpaired from FB Api Connection';
        }
    }

    return errors;
};